import React, { useState } from "react";

import { Grid, IconButton, Tooltip } from "@mui/material";
import { Info, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BsThreeDotsVertical } from "react-icons/bs";

import { useVideoSurveyInventory } from "../../../../../hooks/useVideoSurveyInventory";
import { RoomJob } from "../../../../../models/sales";
import { DefaultMetrics } from "../../../../../util/converts";
import { getMeasurement, getWeightMeasurement } from "../../../../../util/getMeasurement";
import { Input } from "../../../../Form";
import { Colors } from "../../../../Form/Button";
import { useTabComponentActions } from "../../../SurveyCall/TabSection/TabsComponent/useActions";
import useStyles from "./headerStyles";

type HeaderInventoryListProps = {
  room: RoomJob;
  indexRoom: number;
  isExpanded?: boolean;
  handleChangeExpand?: (isExpanded: boolean) => void;
  selectRoom?: (isExpanded: boolean) => void;
  handleClickPhoto?: () => void;
};

export const HeaderInventoryList = ({
  room,
  indexRoom,
  isExpanded,
  handleChangeExpand,
  selectRoom,
  handleClickPhoto,
}: HeaderInventoryListProps) => {
  const { t: translate } = useTranslation();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const { deleteRoom, sumItemsAndMaterials, totalVolume, totalWeight } = useVideoSurveyInventory();

  const { handleUpdateRoom } = useTabComponentActions();

  const classes = useStyles();

  const [isUpdateRoomName, setIsUpdateRoomName] = useState<boolean>(false);
  const [roomName, setRoomName] = useState<string>(room?.name ? room.name : "");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const totalItems = sumItemsAndMaterials({ room, type: "quantity" });

  return (
    <Grid container direction="row" justifyContent="space-between">
      <Grid item xs={4} lg={4} md={4} sm={4}>
        <div data-cy="roomHeader" className={classes.mainHeaderTitle}>
          {isUpdateRoomName ? (
            <div className="d-flex align-items-center">
              <Input
                className={classes.roomInputName}
                name="roomName"
                value={roomName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setRoomName(event.target.value);
                }}
                autoFocus={isUpdateRoomName}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleUpdateRoom(room.name, roomName, indexRoom);
                    setIsUpdateRoomName(false);
                  }
                  if (event.key === "Escape") {
                    setRoomName("");
                    setIsUpdateRoomName(false);
                  }
                }}
              />
            </div>
          ) : (
            <span data-cy="roomName" className={classes.roomItemName}>
              {room?.name || ""}
            </span>
          )}

          {isUpdateRoomName ? (
            <Tooltip title={String(translate("pressEnterToSaveOrEscToCancel"))} placement="right">
              <Info
                htmlColor={Colors.SECONDARY.background.color}
                fontSize="medium"
                style={{ marginLeft: "12px" }}
              />
            </Tooltip>
          ) : (
            <>
              <IconButton
                data-cy={`${room.name.toLowerCase()}-actions`}
                aria-label="more"
                id="long-button"
                aria-haspopup="true"
                onClick={handleClick}
              >
                <BsThreeDotsVertical color="#475467" />
              </IconButton>

              <Menu
                id="long-menu"
                MenuListProps={{
                  "aria-labelledby": "long-button",
                }}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  style: {
                    width: "20ch",
                  },
                }}
              >
                <MenuItem
                  data-cy="addPhoto"
                  key="add-photo"
                  className={classes.menuItem}
                  onClick={() => {
                    if (handleClickPhoto) handleClickPhoto();

                    setAnchorEl(null);
                  }}
                >
                  {translate("addPhoto")}
                </MenuItem>

                <MenuItem
                  data-cy="editRoom"
                  key="edit"
                  className={classes.menuItem}
                  onClick={() => {
                    setRoomName(room?.name || "");
                    setIsUpdateRoomName(true);
                    setAnchorEl(null);
                  }}
                >
                  {translate("edit")}
                </MenuItem>

                <MenuItem
                  data-cy="deleteRoom"
                  key="delete"
                  className={classes.menuItemDelete}
                  onClick={() => {
                    deleteRoom(room);
                    setAnchorEl(null);
                  }}
                >
                  {translate("delete")}
                </MenuItem>
              </Menu>
            </>
          )}
        </div>
      </Grid>
      <Grid item xs={7} lg={7} md={7} sm={7}>
        <section className={classes.sectionItemsInfo}>
          <ul className={classes.roomItemInfo}>
            <li>
              <span className={classes.textInfoRoom}>
                {totalItems} {totalItems > 0 ? translate("items") : translate("item")}
              </span>
            </li>

            <li>
              <span className={classes.textInfoRoom}>
                {`${totalVolume(room)} ${
                  measurement?.code ? measurement?.code.toUpperCase() : DefaultMetrics.VOLUME
                }`}
              </span>
            </li>

            <li>
              <span className={classes.textInfoRoom}>
                {`${Number(totalWeight(room)).toFixed(2)} ${
                  measurementWeight?.code
                    ? measurementWeight?.code?.toUpperCase()
                    : DefaultMetrics.WEIGHT
                }`}
              </span>
            </li>
          </ul>

          <IconButton
            className="p-0"
            data-cy="expandButton"
            onClick={() => {
              if (handleChangeExpand) handleChangeExpand(!isExpanded);
              if (selectRoom) selectRoom(!isExpanded);
            }}
          >
            {isExpanded ? (
              <KeyboardArrowUp htmlColor="#667085" />
            ) : (
              <KeyboardArrowDown htmlColor="#667085" />
            )}
          </IconButton>
        </section>
      </Grid>
    </Grid>
  );
};
